/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { type QueryClient, useQueryClient } from "@tanstack/react-query";

import {
  clearStoredSoilSightProjectId,
  getStoredSoilSightProjectId,
  setStoredSoilSightProjectId,
  useCreateSoilSightProject,
  useTranslation,
  useUpdateSoilSightProjectSettingsRefinements,
} from "utils";

import { GET_SOIL_SIGHT_PROJECT, GET_SOIL_SIGHT_SCENARIO, LIST_SOIL_SIGHT_PROJECTS } from "consts";

import type { DroppedMainCrop, SoilSightProject } from "models";

import { Message } from "components";

import type { useFieldImportLandingState } from "pages/app/scenario-analysis/hooks/useFieldImportLandingState";
import { useProjectDeleteWithUndo } from "pages/app/scenario-analysis/hooks/useProjectDeleteWithUndo.tsx";
import { useSoilSightLocationRegion } from "pages/app/scenario-analysis/hooks/useSoilSightLocationRegion";
import { useSoilSightPageData } from "pages/app/scenario-analysis/hooks/useSoilSightPageData";
import { useSoilSightProjectOrchestration } from "pages/app/scenario-analysis/hooks/useSoilSightProjectOrchestration";
import {
  type AutosaveRefs,
  createSoilSightProjectWithDefaults,
  getNextSoilSightProjectName,
  resetAutosaveRefs,
} from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

/** Resets autosave refs and clears detail caches whenever the project changes (not the scenario list — the gate needs it warm). */
const useResetCachesOnProjectChange = (
  queryClient: QueryClient,
  autosaveRefs: AutosaveRefs,
  soilSightProjectId: string | null,
  setSoilSightCacheReady: Dispatch<SetStateAction<boolean>>
): void => {
  useEffect(
    function resetCachesOnProjectChange() {
      resetAutosaveRefs(autosaveRefs);
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_PROJECT] });
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_SCENARIO] });
      setSoilSightCacheReady(true);
    },
    [soilSightProjectId, queryClient, autosaveRefs, setSoilSightCacheReady]
  );
};

/** Restores the stored project id on mount (no loading flash); a mid-session farm switch resets it so the reconcile adopts the new farm's project. */
const useSoilSightProjectIdFromStorage = (
  klimFarmId: string,
  setSoilSightProjectId: Dispatch<SetStateAction<string | null>>,
  hasHydratedRef: MutableRefObject<boolean>,
  setHydrationApplied: Dispatch<SetStateAction<boolean>>,
  projectEpochRef: MutableRefObject<number>
): void => {
  const prevKlimFarmIdRef = useRef<string | null>(null);
  useLayoutEffect(
    function restoreProjectIdFromStorage() {
      if (!klimFarmId) {
        // Logged out (or no farm): work still out for the previous farm's project must drop its results.
        if (prevKlimFarmIdRef.current !== null) projectEpochRef.current += 1;
        setSoilSightProjectId(null);
        setHydrationApplied(false);
        prevKlimFarmIdRef.current = null;
        return;
      }
      const isFarmSwitch = prevKlimFarmIdRef.current !== null && prevKlimFarmIdRef.current !== klimFarmId;
      prevKlimFarmIdRef.current = klimFarmId;
      if (isFarmSwitch) projectEpochRef.current += 1;
      setSoilSightProjectId(isFarmSwitch ? null : getStoredSoilSightProjectId());
      hasHydratedRef.current = false;
      setHydrationApplied(false);
    },
    [klimFarmId, hasHydratedRef, projectEpochRef, setHydrationApplied, setSoilSightProjectId]
  );
};

type ProjectSessionArgs = {
  klimFarmId: string;
  autosaveRefs: AutosaveRefs;
  hasHydratedRef: MutableRefObject<boolean>;
  setHydrationApplied: Dispatch<SetStateAction<boolean>>;
  /** Resets the page state that belongs to the project being left. */
  onProjectReset: () => void;
  landing: Pick<
    ReturnType<typeof useFieldImportLandingState>,
    "isAwaitingProjectChoice" | "importedFieldCropYearsRef" | "importedSelectedFieldIdRef" | "fieldImportAlreadyPersistedRef" | "dismissProjectEntry"
  >;
  setShowTransitionModal: (show: boolean) => void;
  setAwaitingInitialCalc: (awaiting: boolean) => void;
  setDroppedMainCrops: (crops: DroppedMainCrop[]) => void;
};

/** The active Soil Sight project: which one it is, its server data, and creating, switching, deleting and entering one. */
export const useSoilSightProjectSession = ({
  klimFarmId,
  autosaveRefs,
  hasHydratedRef,
  setHydrationApplied,
  onProjectReset,
  landing,
  setShowTransitionModal,
  setAwaitingInitialCalc,
  setDroppedMainCrops,
}: ProjectSessionArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const queryClient = useQueryClient();
  const soilSightLocationRegion = useSoilSightLocationRegion();

  // Read on first paint so loading shows at once; queries wait for soilSightCacheReady so the cache reset can't race a fetch.
  const [soilSightProjectId, setSoilSightProjectId] = useState<string | null>(() => getStoredSoilSightProjectId());
  // Read by saves after their awaits: the shown project, which storage can run ahead of (an entry-dialog create, another tab).
  const soilSightProjectIdRef = useRef(soilSightProjectId);
  soilSightProjectIdRef.current = soilSightProjectId;
  // Bumped on every project switch so async handlers can drop results that belong to the previous project.
  const projectEpochRef = useRef(0);
  const [soilSightCacheReady, setSoilSightCacheReady] = useState(false);
  const onProjectResetRef = useRef(onProjectReset);
  onProjectResetRef.current = onProjectReset;

  const pageData = useSoilSightPageData({ klimFarmId, soilSightProjectId, soilSightCacheReady });
  const { projects, farmFields, listProjectsQuery } = pageData;

  const { mutateAsync: createProjectAsync } = useCreateSoilSightProject();
  const { mutateAsync: updateSettingsAsync } = useUpdateSoilSightProjectSettingsRefinements();

  useSoilSightProjectIdFromStorage(klimFarmId, setSoilSightProjectId, hasHydratedRef, setHydrationApplied, projectEpochRef);
  useResetCachesOnProjectChange(queryClient, autosaveRefs, soilSightProjectId, setSoilSightCacheReady);

  const getNextProjectName = useCallback(
    function getNextProjectName(): string {
      return getNextSoilSightProjectName(projects, (n) => t("projectManagement.defaultName", { n }));
    },
    [projects, t]
  );

  // Saves that need a project at the same moment (e.g. autosave and a run) share one create instead of making two projects.
  const projectCreateInFlightRef = useRef<Promise<string | null> | null>(null);

  // A project is created on the first edit, so every path that saves resolves (or creates) the id first.
  const ensureProjectId = useCallback(
    async function ensureProjectId(): Promise<string | null> {
      if (soilSightProjectIdRef.current) return soilSightProjectIdRef.current;
      const cached = getStoredSoilSightProjectId();
      if (cached) {
        setSoilSightProjectId(cached);
        return cached;
      }
      if (projectCreateInFlightRef.current) return projectCreateInFlightRef.current;

      const createProject = async (): Promise<string | null> => {
        // Project defaults; the project-settings drawer refines them later.
        const created = await createProjectAsync({
          farmId: klimFarmId,
          locationRegion: soilSightLocationRegion,
          referenceName: t("scenarioBuilder.reference"),
          name: getNextProjectName(),
        });
        if (created?.soilSightProjectId) setStoredSoilSightProjectId(created.soilSightProjectId);
        soilSightProjectIdRef.current = created?.soilSightProjectId ?? null;
        setSoilSightProjectId(created?.soilSightProjectId ?? null);
        return created?.soilSightProjectId ?? null;
      };
      projectCreateInFlightRef.current = createProject().finally(() => {
        projectCreateInFlightRef.current = null;
      });
      return projectCreateInFlightRef.current;
    },
    [createProjectAsync, getNextProjectName, klimFarmId, soilSightLocationRegion, t]
  );

  const reloadSoilSightProject = useCallback(
    function reloadSoilSightProject(newProjectId: string) {
      projectEpochRef.current += 1;
      soilSightProjectIdRef.current = newProjectId;
      setStoredSoilSightProjectId(newProjectId);
      resetAutosaveRefs(autosaveRefs);
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_PROJECT] });
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_SCENARIO] });
      setHydrationApplied(false);
      onProjectResetRef.current();

      const isSameProject = newProjectId === soilSightProjectId;
      if (isSameProject) {
        // The cache reset only runs on an id change, so reloading the active project re-hydrates here.
        setSoilSightCacheReady(true);
      } else {
        setSoilSightCacheReady(false);
        setSoilSightProjectId(newProjectId);
      }
    },
    [autosaveRefs, queryClient, setHydrationApplied, soilSightProjectId]
  );

  // The "no project" reset: the last project was deleted, or the stored one is no longer the farm's.
  const clearActiveProject = useCallback(
    function clearActiveProject() {
      projectEpochRef.current += 1;
      soilSightProjectIdRef.current = null;
      clearStoredSoilSightProjectId();
      setSoilSightProjectId(null);
      setHydrationApplied(false);
      setSoilSightCacheReady(false);
      hasHydratedRef.current = false;
    },
    [hasHydratedRef, setHydrationApplied]
  );

  const handleBeforeProjectDeleted = useCallback(
    function handleBeforeProjectDeleted() {
      // Disabled before the delete fires, so no refetch hits the soft-deleted project.
      setSoilSightCacheReady(false);
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_PROJECT] });
      queryClient.removeQueries({ queryKey: [GET_SOIL_SIGHT_SCENARIO] });
    },
    [queryClient]
  );

  // After the last project's undo window expires; unlike ensureProjectId it never returns the deleted id.
  const createBlankProjectAfterDeletion = useCallback(
    async function createBlankProjectAfterDeletion(excludedProjectIds: string[]): Promise<string | null> {
      // Pending deletions are excluded so the new name takes the smallest free slot.
      const excludedSet = new Set(excludedProjectIds);
      const allProjects = queryClient.getQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId]) ?? [];
      const effectiveProjects = allProjects.filter((project) => !excludedSet.has(project.id));
      const projectName = getNextSoilSightProjectName(effectiveProjects, (n) => t("projectManagement.defaultName", { n }));

      const createdProjectId = await createSoilSightProjectWithDefaults({
        body: { farmId: klimFarmId, locationRegion: soilSightLocationRegion, referenceName: t("scenarioBuilder.reference"), name: projectName },
        fields: farmFields,
        create: createProjectAsync,
        assignField: (args) => updateSettingsAsync({ ...args, klimFarmId }),
      });
      if (createdProjectId) {
        // Added to the list cache now, or ProjectManagement shows "Unnamed project" until the refetch lands.
        queryClient.setQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId], (old) => [
          ...(old ?? []),
          { id: createdProjectId, name: projectName, scenarioCount: 1, fieldId: null, createdAt: "" },
        ]);
        reloadSoilSightProject(createdProjectId);
      }
      return createdProjectId;
    },
    [createProjectAsync, farmFields, klimFarmId, queryClient, reloadSoilSightProject, soilSightLocationRegion, t, updateSettingsAsync]
  );

  const projectEntry = useSoilSightProjectOrchestration({
    hasFcpoFlowInitial: landing.isAwaitingProjectChoice,
    listProjectsQuerySuccess: listProjectsQuery.isSuccess,
    klimFarmId,
    importedFieldCropYearsRef: landing.importedFieldCropYearsRef,
    importedSelectedFieldIdRef: landing.importedSelectedFieldIdRef,
    fieldImportAlreadyPersistedRef: landing.fieldImportAlreadyPersistedRef,
    farmFields,
    getNextProjectName,
    switchProject: reloadSoilSightProject,
    onProjectCreated: reloadSoilSightProject,
    setShowTransitionModal,
    setAwaitingInitialCalc,
    setDroppedMainCrops,
    onDismiss: landing.dismissProjectEntry,
  });

  const { pendingDeleteIds, handleDelete: handleDeleteProject } = useProjectDeleteWithUndo({
    klimFarmId,
    resolvedActiveId: soilSightProjectId,
    onBeforeDelete: handleBeforeProjectDeleted,
    onSwitch: reloadSoilSightProject,
    onCreateBlank: createBlankProjectAfterDeletion,
    onLastDeleted: clearActiveProject,
  });

  return {
    soilSightProjectId,
    soilSightCacheReady,
    projectEpochRef,
    ensureProjectId,
    pageData,
    projectEntry,
    pendingDeleteIds,
    handleDeleteProject,
    reloadSoilSightProject,
    clearActiveProject,
  };
};

type LeaveProjectWithFlushArgs = {
  hasHydratedRef: MutableRefObject<boolean>;
  /** Resolves false when a save failed (toast shown). */
  flushUnsavedChanges: () => Promise<boolean>;
  /** True once per failed save, so a save that keeps failing can't trap the user on the project. */
  shouldBlockSwitchAfterFailedSave: () => boolean;
  reloadSoilSightProject: (projectId: string) => void;
  /** True while an entry-dialog choice is still saving; it lands on its own project afterwards, so a header switch then would be overridden. */
  isEntryActionRunningRef: MutableRefObject<boolean>;
};

/** Leaving the project from the header saves its unsaved edits first, or those still debouncing (or failed) are dropped. */
export const useLeaveProjectWithFlush = ({
  hasHydratedRef,
  flushUnsavedChanges,
  shouldBlockSwitchAfterFailedSave,
  reloadSoilSightProject,
  isEntryActionRunningRef,
}: LeaveProjectWithFlushArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  // Bumped to reset the header's own selection when a switch is blocked.
  const [projectHeaderVersion, setProjectHeaderVersion] = useState(0);

  // Read through a ref so the header callbacks (and navigationCrumbs) stay stable when the project list changes.
  const flushUnsavedChangesRef = useRef(flushUnsavedChanges);
  flushUnsavedChangesRef.current = flushUnsavedChanges;

  const saveBeforeLeavingProject = useCallback(
    async function saveBeforeLeavingProject(): Promise<boolean> {
      if (!hasHydratedRef.current) return true;
      return flushUnsavedChangesRef.current();
    },
    [hasHydratedRef]
  );

  const handleUserSwitchProject = useCallback(
    async function handleUserSwitchProject(newProjectId: string) {
      if (isEntryActionRunningRef.current) {
        setProjectHeaderVersion((version) => version + 1);
        return;
      }
      const isSaved = await saveBeforeLeavingProject();
      if (!isSaved && shouldBlockSwitchAfterFailedSave()) {
        setProjectHeaderVersion((version) => version + 1);
        // The next switch goes through, so the user is told it leaves the unsaved changes behind.
        Message({ content: t("projectManagement.switchAgainToLeave"), dataTestId: "switch-again-to-leave-message" });
        return;
      }
      reloadSoilSightProject(newProjectId);
    },
    [isEntryActionRunningRef, reloadSoilSightProject, saveBeforeLeavingProject, shouldBlockSwitchAfterFailedSave, t]
  );

  // The new project already exists on the server, so the user lands on it even if saving failed.
  const handleUserProjectCreated = useCallback(
    async function handleUserProjectCreated(newProjectId: string) {
      await saveBeforeLeavingProject();
      reloadSoilSightProject(newProjectId);
    },
    [reloadSoilSightProject, saveBeforeLeavingProject]
  );

  return { projectHeaderVersion, saveBeforeLeavingProject, handleUserSwitchProject, handleUserProjectCreated };
};

type ReconcileArgs = {
  klimFarmId: string;
  soilSightProjectId: string | null;
  projects: SoilSightProject[];
  isProjectListLoaded: boolean;
  pendingDeleteIds: Set<string>;
  reloadSoilSightProject: (projectId: string) => void;
  clearActiveProject: () => void;
};

/** Keeps the active project one of the farm's; called after hydration so a stale project's load is reset after it lands. */
export const useReconcileActiveProjectWithProjectList = ({
  klimFarmId,
  soilSightProjectId,
  projects,
  isProjectListLoaded,
  pendingDeleteIds,
  reloadSoilSightProject,
  clearActiveProject,
}: ReconcileArgs): void => {
  useEffect(
    function reconcileStaleActiveProjectWithProjectList() {
      if (!isProjectListLoaded || !klimFarmId) return;

      const visibleProjects = projects.filter((project) => !pendingDeleteIds.has(project.id));

      // Active id already belongs to this farm's list — nothing to reconcile.
      if (soilSightProjectId && visibleProjects.some((project) => project.id === soilSightProjectId)) return;

      // Absent or foreign id: adopt the most recently touched project (updatedAt, else createdAt).
      if (visibleProjects.length > 0) {
        const projectToAdopt = visibleProjects.reduce((best, project) => {
          const projectTs = new Date(project.updatedAt ?? project.createdAt).getTime();
          const bestTs = new Date(best.updatedAt ?? best.createdAt).getTime();
          return projectTs > bestTs ? project : best;
        });
        reloadSoilSightProject(projectToAdopt.id);
        return;
      }

      // No projects left — reset to the empty state only when something was active.
      if (soilSightProjectId) clearActiveProject();
    },
    [clearActiveProject, projects, klimFarmId, isProjectListLoaded, pendingDeleteIds, reloadSoilSightProject, soilSightProjectId]
  );
};
